<div class="details-dialog">
  <div class="dialog-header">
    <h2>IA Copy Report</h2>
    <button mat-icon-button (click)="closeDialog()" class="close-btn">
      <mat-icon>close</mat-icon>
    </button>
  </div>

  <div class="dialog-content">
    <div class="ia-copy-loading" *ngIf="loading">
      <mat-spinner [diameter]="28" [strokeWidth]="3"></mat-spinner>
      <span>Loading IA copy details...</span>
    </div>

    <div class="ia-copy-error" *ngIf="!loading && error">{{ error }}</div>

    <ng-container *ngIf="!loading && iaCopyStatus as status">
      <!-- General summary -->
      <div class="report-section">
        <h3>Summary</h3>
        <div class="info-grid">
          <div class="info-row">
            <span class="info-label">File Name:</span>
            <span class="info-value">{{ fileName }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Status:</span>
            <span class="info-value">
              <span class="badge" [ngClass]="statusClass(status.Status)">{{ status.Status }}</span>
            </span>
          </div>
          <div class="info-row">
            <span class="info-label">Total Offers:</span>
            <span class="info-value">{{ status.TotalOffers }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Invalid Offers:</span>
            <span class="info-value">{{ status.InvalidOfferClientKeys?.length || 0 }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Already Pending:</span>
            <span class="info-value">{{ status.OfferClientKeysWithPendingIARequest?.length || 0 }}</span>
          </div>
          <div class="info-row" *ngIf="status.CreatedOn">
            <span class="info-label">Created:</span>
            <span class="info-value">{{ status.CreatedOn | date:'medium' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Time Elapsed:</span>
            <span class="info-value" [title]="status.TimeElapsed + ' ms'">{{ formatDuration(status.TimeElapsed) }}</span>
          </div>
        </div>
      </div>

      <!-- Invalid offer keys -->
      <div class="report-section" *ngIf="status.InvalidOfferClientKeys?.length">
        <h3>Invalid Offer Keys ({{ status.InvalidOfferClientKeys.length }})</h3>
        <ul class="key-list">
          <li *ngFor="let key of status.InvalidOfferClientKeys">{{ key }}</li>
        </ul>
      </div>

      <!-- Already has a pending IA copy request -->
      <div class="report-section" *ngIf="status.OfferClientKeysWithPendingIARequest?.length">
        <h3>Already Has IA Copy Request Pending ({{ status.OfferClientKeysWithPendingIARequest.length }})</h3>
        <ul class="key-list">
          <li *ngFor="let key of status.OfferClientKeysWithPendingIARequest">{{ key }}</li>
        </ul>
      </div>

      <!-- Provider-wise summary -->
      <div class="report-section">
        <h3>Provider Summary</h3>
        <div class="table-scroll" *ngIf="providers.length; else noProviders">
          <table class="report-table">
            <thead>
              <tr>
                <th>Provider</th>
                <th>Status</th>
                <th>Total</th>
                <th>Success</th>
                <th>Failed</th>
                <th>Invalid</th>
                <th>Time</th>
                <th>Batch Id</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let p of providers">
                <td class="wrap-cell" [title]="p.ProviderName || p.ProviderId">{{ p.ProviderName || p.ProviderId }}</td>
                <td [title]="p.Status || '-'"><span class="badge" [ngClass]="statusClass(p.Status)">{{ p.Status || '-' }}</span></td>
                <td [title]="p.TotalItemsCount">{{ p.TotalItemsCount }}</td>
                <td [title]="p.SuccessItemsCount">{{ p.SuccessItemsCount }}</td>
                <td [title]="p.FailedItemsCount">{{ p.FailedItemsCount }}</td>
                <td [title]="p.ItemsWithValidationErrorsCount">{{ p.ItemsWithValidationErrorsCount }}</td>
                <td [title]="p.TimeElapsed + ' ms'">{{ formatDuration(p.TimeElapsed) }}</td>
                <td class="nowrap-cell mono" [title]="p.BatchId || '-'">{{ p.BatchId || '-' }}</td>
                <td class="wrap-cell" [title]="p.Error || '-'">{{ p.Error || '-' }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <ng-template #noProviders>
          <p class="no-data">No provider results.</p>
        </ng-template>
      </div>

      <!-- Offers summary: offer × provider matrix -->
      <div class="report-section">
        <h3>Offers Summary</h3>
        <div class="table-scroll" *ngIf="status.OfferResults?.length; else noOffers">
          <table class="report-table matrix">
            <thead>
              <tr>
                <th>Offer Id</th>
                <th>Client Key</th>
                <th *ngFor="let p of providers">{{ p.ProviderName || p.ProviderId }}</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let offer of status.OfferResults">
                <td class="wrap-cell mono" [title]="offer.OfferId || '-'">{{ offer.OfferId || '-' }}</td>
                <td class="wrap-cell" [title]="offer.OfferClientKey || '-'">{{ offer.OfferClientKey || '-' }}</td>
                <td *ngFor="let p of providers">
                  <ng-container *ngIf="responseFor(offer, p.ProviderId) as r; else noResp">
                    <div class="provider-cell" [ngClass]="statusClass(r.Status)">
                      <div class="cell-line"><span class="cell-key">Status:</span> {{ r.Status || '-' }}</div>
                      <div class="cell-line"><span class="cell-key">Generation Id:</span> <span class="mono">{{ r.GenerationId || '-' }}</span></div>
                      <div class="cell-line" *ngIf="r.Error"><span class="cell-key">Error:</span> {{ r.Error }}</div>
                      <div class="cell-line from-history" *ngIf="r.IsResponseFromHistory">From history</div>
                    </div>
                  </ng-container>
                  <ng-template #noResp>
                    <div class="provider-cell status-none">
                      <div class="cell-line">-</div>
                    </div>
                  </ng-template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <ng-template #noOffers>
          <p class="no-data">No offer results.</p>
        </ng-template>
      </div>

      <!-- Info logs (shown last, after all summaries) -->
      <div class="report-section" *ngIf="status.Info?.length">
        <h3>Info</h3>
        <ul class="message-list info">
          <li *ngFor="let msg of status.Info">{{ msg }}</li>
        </ul>
      </div>
    </ng-container>
  </div>

  <div class="dialog-footer">
    <button mat-raised-button color="primary" (click)="closeDialog()">Close</button>
  </div>
</div>
